import { CompanyDetailsInterface } from '../../../services/exports/Interfaces';
import dayjs, { BusinessHoursMap } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

type Props = {
  workSchedule: NonNullable<CompanyDetailsInterface['work_schedule']>;
  companyTz: string;
  userTz: string;
  scheduleAheadTime?: number;
  lastOrderGap?: number;
  withTimeOffsets?: boolean;
};

export const buildBusinessTimes = ({
  scheduleAheadTime,
  workSchedule,
  lastOrderGap,
  userTz,
  companyTz,
  withTimeOffsets,
}: Props): BusinessHoursMap => {
  const businessTimes: BusinessHoursMap = {
    monday: null,
    tuesday: null,
    wednesday: null,
    thursday: null,
    friday: null,
    saturday: null,
    sunday: null,
  };

  const weekStart = dayjs().tz(companyTz).startOf('isoWeek');

  for (const entry of workSchedule) {
    const openDay = entry.open.day === 0 ? 6 : entry.open.day - 1;
    const closeDay = entry.close.day === 0 ? 6 : entry.close.day - 1;
    let openTime = weekStart
      .clone()
      .add(openDay, 'days')
      .hour(entry.open.hour)
      .minute(entry.open.minute)
      .second(0)
      .millisecond(0)
      .tz(userTz);
    let closeTime = weekStart
      .clone()
      .add(closeDay, 'days')
      .hour(entry.close.hour)
      .minute(entry.close.minute)
      .second(0)
      .millisecond(0)
      .tz(userTz);

    if (withTimeOffsets) {
      openTime = openTime.add(scheduleAheadTime, 'minute');
      closeTime = closeTime.subtract(lastOrderGap, 'minute');
    }

    const openKey = openTime.locale('en').format('dddd').toLowerCase();
    const closeKey = closeTime.format('dddd').toLowerCase();
    const start = openTime.format('HH:mm:ss');
    const end = closeTime.format('HH:mm:ss');

    if (openKey === closeKey) {
      businessTimes[openKey] ??= [];
      businessTimes[openKey].push({ start, end });
      continue;
    }

    businessTimes[openKey] ??= [];
    businessTimes[openKey].push({ start, end: '23:59:59' });

    businessTimes[closeKey] ??= [];
    businessTimes[closeKey].push({ start: '00:00:00', end });
  }

  return businessTimes;
};
